import { type FieldValues, type UseControllerProps, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Switcher, type SwitcherProps } from '@wf/components';
import { Tooltip } from '@wf/sw-ui-kit';

import { type LicenseFieldProps } from './LicenseFieldController';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import { useLicenseController } from '../../hooks/useLicenseController';

export interface ISwitcherOption {
  id: string;
  text: string;
  helpText?: string;
  disabled?: boolean;
}

export type ILicenseSwitcherProps = Omit<SwitcherProps, 'onChange' | 'value' | 'children'> & {
  options: ISwitcherOption[];
  onChange?: SwitcherProps['onChange'];
  value?: SwitcherProps['value'];
};

export const LicenseSwitcher = ({
  name,
  defaultValue,
  configName,
  featureFlag,
  viewMode,
  rules,
  shouldUnregister,
  control,
  options,
  disabled,
  ...rest
}: ILicenseSwitcherProps & LicenseFieldProps & UseControllerProps<FieldValues, any>) => {
  const licenseAbbreviation = useLicenseAbbreviation();
  const { t } = useTranslation();
  const { control: contextControl } = useFormContext();

  const {
    field: { value, onChange, ...fieldRest },
    computedProps,
    config,
  } = useLicenseController({
    name,
    configName,
    featureFlag,
    control: control ?? contextControl,
    defaultValue,
    rules,
    shouldUnregister,
    viewMode,
    disabled,
  });

  const handleChange: typeof rest.onChange = (...args) => {
    onChange(...args);

    if ('onChange' in rest && typeof rest.onChange === 'function') {
      rest.onChange(...args);
    }
  };

  if (config.isHidden) {
    return <div></div>;
  }

  return (
    <Switcher value={value} {...fieldRest} {...computedProps} {...rest} valueView={t(value)} onChange={handleChange}>
      {options.map(({ id, text, helpText, disabled }) => (
        <Switcher.Item key={id} id={id} disabled={disabled}>
          {helpText ? (
            <Tooltip tooltipContent={t(helpText, { licenseAbbreviation })} placement="bottom">
              {t(text)}
            </Tooltip>
          ) : (
            t(text)
          )}
        </Switcher.Item>
      ))}
    </Switcher>
  );
};
